import React, { useEffect, useMemo, useState } from 'react'
import { ArrowDownIcon, ArrowRightIcon, ArrowUpIcon } from '@heroicons/react/24/outline'
import { Input, Text } from '../../components/ui'
import { estoqueService } from '../../services/estoque'
import { useUserLogs } from '../../hooks/useUserLogs'
import type { EstoqueItem, EstoqueLocal, EstoqueMovementType } from '../../types/estoque'
import EstoqueModal, { EstoqueModalActions } from './EstoqueModal'
import EstoqueItemCombobox from './EstoqueItemCombobox'
import { SELECT_CLASS, TEXTAREA_CLASS, formatQuantity, movementTypeLabel } from './estoqueUi'

type Props = {
  open: boolean
  items: EstoqueItem[]
  locais: EstoqueLocal[]
  initialItemId?: string
  initialLocalId?: string
  onClose: () => void
  onSaved: () => void
}

function qtyClass(delta: number | null): string {
  if (delta == null || delta === 0) return 'text-gray-900 dark:text-white'
  if (delta > 0) return 'text-green-700 dark:text-green-300'
  return 'text-red-700 dark:text-red-300'
}

const EstoqueMovimentacaoModal: React.FC<Props> = ({
  open,
  items,
  locais,
  initialItemId = '',
  initialLocalId = '',
  onClose,
  onSaved,
}) => {
  const { logCreate } = useUserLogs()
  const [itemId, setItemId] = useState(initialItemId)
  const [localId, setLocalId] = useState(initialLocalId)
  const [toLocalId, setToLocalId] = useState('')
  const [movementType, setMovementType] = useState<EstoqueMovementType>('in')
  const [quantity, setQuantity] = useState(1)
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [qtyByLocation, setQtyByLocation] = useState<Record<string, number>>({})
  const [loadingSaldo, setLoadingSaldo] = useState(false)
  const [saldosReady, setSaldosReady] = useState(false)
  const activeItems = items.filter((i) => i.is_active)
  const activeLocais = locais.filter((l) => l.is_active)
  const isTransfer = movementType === 'transfer'

  useEffect(() => {
    if (!open) return
    setItemId(initialItemId || '')
    setLocalId(initialLocalId || '')
    setToLocalId('')
    setMovementType('in')
    setQuantity(1)
    setDescription('')
    setError(null)
    setQtyByLocation({})
    setSaldosReady(false)
  }, [open, initialItemId, initialLocalId])

  useEffect(() => {
    if (!open || !itemId) {
      setQtyByLocation({})
      setSaldosReady(false)
      setLoadingSaldo(false)
      return
    }
    let cancelled = false
    ;(async () => {
      setLoadingSaldo(true)
      setSaldosReady(false)
      try {
        const saldos = await estoqueService.listSaldos()
        if (cancelled) return
        const map: Record<string, number> = {}
        for (const saldo of saldos) {
          if (saldo.item_id === itemId) {
            map[saldo.location_id] = Number(saldo.quantity)
          }
        }
        setQtyByLocation(map)
        setSaldosReady(true)
      } catch {
        if (!cancelled) {
          setQtyByLocation({})
          setSaldosReady(false)
        }
      } finally {
        if (!cancelled) setLoadingSaldo(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, itemId])

  const selectedItem = activeItems.find((i) => i.id === itemId)
  const selectedLocal = activeLocais.find((l) => l.id === localId)
  const selectedToLocal = activeLocais.find((l) => l.id === toLocalId)
  const unit = selectedItem?.unit || 'un'
  const qty = Number.isFinite(quantity) ? quantity : 0
  const fromQty = itemId && localId && saldosReady ? (qtyByLocation[localId] ?? 0) : null
  const toQty = itemId && toLocalId && saldosReady ? (qtyByLocation[toLocalId] ?? 0) : null

  const locationLabel = (localName: string, locationKey: string) => {
    if (!itemId || !saldosReady) return localName
    const current = qtyByLocation[locationKey] ?? 0
    return `${localName} (${formatQuantity(current)})`
  }

  const simulation = useMemo(() => {
    if (fromQty == null || !itemId || !localId) return null

    if (isTransfer) {
      const fromAfter = fromQty - qty
      const toBefore = toQty
      const toAfter = toBefore == null ? null : toBefore + qty
      return {
        mode: 'transfer' as const,
        fromBefore: fromQty,
        fromAfter,
        fromDelta: -qty,
        toBefore,
        toAfter,
        toDelta: qty,
        wouldGoNegative: qty > 0 && fromAfter < 0,
      }
    }

    let after = fromQty
    let delta = 0
    if (movementType === 'in') {
      after = fromQty + qty
      delta = qty
    } else if (movementType === 'out') {
      after = fromQty - qty
      delta = -qty
    } else {
      after = qty
      delta = after - fromQty
    }

    return {
      mode: 'single' as const,
      fromBefore: fromQty,
      fromAfter: after,
      fromDelta: delta,
      toBefore: null as number | null,
      toAfter: null as number | null,
      toDelta: null as number | null,
      wouldGoNegative:
        (movementType === 'out' && qty > 0 && after < 0) ||
        (movementType === 'adjustment' && after < 0),
    }
  }, [fromQty, toQty, itemId, localId, isTransfer, movementType, qty])

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!itemId || !localId) {
      setError(isTransfer ? 'Selecione o item e o local de origem.' : 'Selecione o item e o local.')
      return
    }
    if (isTransfer && !toLocalId) {
      setError('Selecione o local de destino.')
      return
    }
    if (isTransfer && toLocalId === localId) {
      setError('Local de origem e destino devem ser diferentes.')
      return
    }
    if (!description.trim()) {
      setError('Descrição é obrigatória.')
      return
    }
    if (movementType !== 'adjustment' && quantity <= 0) {
      setError('Quantidade deve ser maior que zero.')
      return
    }
    if (movementType === 'adjustment' && quantity < 0) {
      setError('Novo valor de estoque deve ser maior ou igual a zero.')
      return
    }
    if (simulation?.wouldGoNegative) {
      setError('Operação deixaria o estoque negativo no local.')
      return
    }
    setSaving(true)
    try {
      if (isTransfer) {
        const movements = await estoqueService.transferMovimentacao({
          item_id: itemId,
          from_location_id: localId,
          to_location_id: toLocalId,
          quantity,
          description: description.trim(),
        })
        const primary = movements[0]
        void logCreate(
          'marketing_stock_movement',
          primary?.id ?? itemId,
          `Registrou transferência de estoque: ${selectedItem?.name ?? itemId}`,
          {
            item_id: itemId,
            item_name: selectedItem?.name ?? null,
            from_location_id: localId,
            from_location_name: selectedLocal?.name ?? null,
            to_location_id: toLocalId,
            to_location_name: selectedToLocal?.name ?? null,
            movement_type: 'transfer',
            quantity,
            movement_ids: movements.map((m) => m.id),
            description: description.trim(),
          }
        )
      } else {
        const movement = await estoqueService.registerMovimentacao({
          item_id: itemId,
          location_id: localId,
          movement_type: movementType,
          quantity,
          description: description.trim(),
        })
        void logCreate(
          'marketing_stock_movement',
          movement.id,
          `Registrou ${movementTypeLabel(movementType).toLowerCase()} de estoque: ${selectedItem?.name ?? itemId}`,
          {
            item_id: itemId,
            item_name: selectedItem?.name ?? null,
            location_id: localId,
            location_name: selectedLocal?.name ?? null,
            movement_type: movementType,
            quantity,
            quantity_before: movement.quantity_before,
            quantity_after: movement.quantity_after,
            description: description.trim(),
          }
        )
      }
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao registrar movimentação.')
    } finally {
      setSaving(false)
    }
  }

  const deltaLabel = (delta: number) => {
    if (delta > 0) return `+${formatQuantity(delta)}`
    return formatQuantity(delta)
  }

  const wouldGoNegative = Boolean(simulation?.wouldGoNegative)
  const confirmLabel = wouldGoNegative ? 'Estoque ficaria negativo' : 'Confirmar'
  const confirmTitle = wouldGoNegative
    ? 'Não é permitido deixar o estoque negativo em nenhum local'
    : undefined

  return (
    <EstoqueModal
      title="Movimentar Estoque Manualmente"
      onClose={onClose}
      busy={saving}
      onSubmit={handleSubmit}
      maxWidthClass="max-w-3xl"
      footer={
        <EstoqueModalActions
          onCancel={onClose}
          saving={saving}
          submitLabel={confirmLabel}
          submitDisabled={wouldGoNegative}
          submitTitle={confirmTitle}
        />
      }
    >
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        {/* Painel de simulação */}
        <aside className="lg:col-span-2 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
          <Text size="sm" className="mb-3 font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Simulação do saldo
          </Text>

          {!itemId || !localId ? (
            <Text size="sm" color="muted">
              Selecione o item e o local para ver o impacto no estoque.
            </Text>
          ) : loadingSaldo && fromQty == null ? (
            <Text size="sm" color="muted">
              Carregando saldo atual...
            </Text>
          ) : simulation ? (
            <div className="space-y-4">
              {selectedItem && (
                <div>
                  <Text size="xs" color="muted">
                    Item
                  </Text>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedItem.name}</p>
                </div>
              )}

              {simulation.mode === 'single' ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                    <Text size="xs" color="muted">
                      {selectedLocal?.name || 'Local'}
                    </Text>
                    <div className="mt-2 flex items-end justify-between gap-2">
                      <div>
                        <Text size="xs" color="muted">
                          Atual
                        </Text>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatQuantity(simulation.fromBefore)}{' '}
                          <span className="text-sm font-normal text-gray-500">{unit}</span>
                        </p>
                      </div>
                      <ArrowRightIcon className="mb-2 h-4 w-4 shrink-0 text-gray-400" />
                      <div className="text-right">
                        <Text size="xs" color="muted">
                          Após gravar
                        </Text>
                        <p className={`text-lg font-semibold ${qtyClass(simulation.fromDelta)}`}>
                          {formatQuantity(simulation.fromAfter)}{' '}
                          <span className="text-sm font-normal text-gray-500">{unit}</span>
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-1.5 text-sm">
                      {simulation.fromDelta > 0 ? (
                        <ArrowUpIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                      ) : simulation.fromDelta < 0 ? (
                        <ArrowDownIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
                      ) : null}
                      <span className={qtyClass(simulation.fromDelta)}>
                        {simulation.fromDelta === 0
                          ? 'Sem alteração'
                          : simulation.fromDelta > 0
                            ? `Inclui ${formatQuantity(simulation.fromDelta)} ${unit}`
                            : `Diminui ${formatQuantity(Math.abs(simulation.fromDelta))} ${unit}`}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-lg border border-red-200 bg-white p-3 dark:border-red-900/40 dark:bg-gray-800">
                    <Text size="xs" className="font-medium text-red-700 dark:text-red-300">
                      Sai de {selectedLocal?.name || 'origem'}
                    </Text>
                    <div className="mt-2 flex items-end justify-between gap-2">
                      <div>
                        <Text size="xs" color="muted">
                          Atual
                        </Text>
                        <p className="text-base font-semibold text-gray-900 dark:text-white">
                          {formatQuantity(simulation.fromBefore)} {unit}
                        </p>
                      </div>
                      <ArrowRightIcon className="mb-1 h-4 w-4 shrink-0 text-gray-400" />
                      <div className="text-right">
                        <Text size="xs" color="muted">
                          Após
                        </Text>
                        <p className={`text-base font-semibold ${qtyClass(simulation.fromDelta)}`}>
                          {formatQuantity(simulation.fromAfter)} {unit}
                        </p>
                      </div>
                    </div>
                    <p className={`mt-2 text-sm ${qtyClass(simulation.fromDelta)}`}>
                      {deltaLabel(simulation.fromDelta)} {unit}
                    </p>
                  </div>

                  <div className="flex justify-center">
                    <ArrowDownIcon className="h-5 w-5 text-sky-600 dark:text-sky-400" />
                  </div>

                  <div className="rounded-lg border border-green-200 bg-white p-3 dark:border-green-900/40 dark:bg-gray-800">
                    <Text size="xs" className="font-medium text-green-700 dark:text-green-300">
                      Entra em {selectedToLocal?.name || 'destino'}
                    </Text>
                    {simulation.toBefore == null || simulation.toAfter == null ? (
                      <Text size="sm" color="muted" className="mt-2">
                        Selecione o local de destino.
                      </Text>
                    ) : (
                      <>
                        <div className="mt-2 flex items-end justify-between gap-2">
                          <div>
                            <Text size="xs" color="muted">
                              Atual
                            </Text>
                            <p className="text-base font-semibold text-gray-900 dark:text-white">
                              {formatQuantity(simulation.toBefore)} {unit}
                            </p>
                          </div>
                          <ArrowRightIcon className="mb-1 h-4 w-4 shrink-0 text-gray-400" />
                          <div className="text-right">
                            <Text size="xs" color="muted">
                              Após
                            </Text>
                            <p className={`text-base font-semibold ${qtyClass(simulation.toDelta)}`}>
                              {formatQuantity(simulation.toAfter)} {unit}
                            </p>
                          </div>
                        </div>
                        <p className={`mt-2 text-sm ${qtyClass(simulation.toDelta)}`}>
                          {deltaLabel(simulation.toDelta ?? 0)} {unit}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {simulation.wouldGoNegative && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  Esta operação deixaria o estoque negativo. Ajuste a quantidade para continuar.
                </div>
              )}

              <Text size="xs" color="muted">
                Valores simulados — só são gravados ao confirmar.
              </Text>
            </div>
          ) : (
            <Text size="sm" color="muted">
              Não foi possível carregar o saldo.
            </Text>
          )}
        </aside>

        {/* Formulário */}
        <div className="space-y-4 lg:col-span-3">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Item <span className="text-red-500">*</span>
            </label>
            <EstoqueItemCombobox
              items={activeItems}
              value={itemId}
              onChange={setItemId}
              required
            />
          </div>
          <div>
            <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Tipo <span className="text-red-500">*</span>
            </span>
            <div className="flex flex-wrap gap-4 sm:gap-x-6 sm:gap-y-2">
              {(
                [
                  { value: 'in', label: 'Entrada' },
                  { value: 'out', label: 'Saída' },
                  { value: 'transfer', label: 'Transferência entre locais' },
                  { value: 'adjustment', label: 'Ajuste' },
                ] as const
              ).map((option) => (
                <label key={option.value} className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="estoque-movement-type"
                    value={option.value}
                    checked={movementType === option.value}
                    onChange={() => {
                      setMovementType(option.value)
                      if (option.value !== 'transfer') setToLocalId('')
                    }}
                    className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {isTransfer ? 'Local de origem' : 'Local'} <span className="text-red-500">*</span>
            </label>
            <select
              value={localId}
              onChange={(e) => setLocalId(e.target.value)}
              className={`w-full ${SELECT_CLASS}`}
              required
            >
              <option value="">Selecione</option>
              {activeLocais.map((local) => (
                <option key={local.id} value={local.id}>
                  {locationLabel(local.name, local.id)}
                </option>
              ))}
            </select>
          </div>
          {isTransfer && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Local de destino <span className="text-red-500">*</span>
              </label>
              <select
                value={toLocalId}
                onChange={(e) => setToLocalId(e.target.value)}
                className={`w-full ${SELECT_CLASS}`}
                required
              >
                <option value="">Selecione</option>
                {activeLocais
                  .filter((local) => local.id !== localId)
                  .map((local) => (
                    <option key={local.id} value={local.id}>
                      {locationLabel(local.name, local.id)}
                    </option>
                  ))}
              </select>
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {movementType === 'adjustment' ? 'Novo valor de estoque *' : 'Quantidade *'}
            </label>
            <Input
              type="number"
              min={0}
              step="any"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Descrição <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={TEXTAREA_CLASS}
              required
            />
          </div>
        </div>
      </div>
    </EstoqueModal>
  )
}

export default EstoqueMovimentacaoModal
